import { useEffect, useState } from 'react';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import { CONTEXT_PROD_MODE, INTERACTIVE_SERVICE_BASE_URL } from '../constants';

export const CONTEXT_ENVS_MAX_ATTEMPTS = 3;
export const CONTEXT_ENVS_RETRY_DELAY_MS = 2000;

const fetchEnvs = async signal => {
    const res = await interactiveServiceFetch(`${INTERACTIVE_SERVICE_BASE_URL}/context-envs`, { signal });
    if (!res.ok) throw new Error(`context-envs responded ${res.status}`);
    const data = await res.json();
    if (!data.envs?.length) throw new Error('context-envs returned no envs');
    return data.envs;
};

// The static context envs in S3: prod plus one per singular-ai-context branch.
// Retries, so one transient S3 or network failure does not leave the admin with prod only.
const useContextEnvs = () => {
    const [envs, setEnvs] = useState([CONTEXT_PROD_MODE]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        (async () => {
            for (let attempt = 1; attempt <= CONTEXT_ENVS_MAX_ATTEMPTS; attempt += 1) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    setEnvs(await fetchEnvs(controller.signal));
                    setLoading(false);
                    return;
                } catch (err) {
                    if (controller.signal.aborted) return;
                    if (attempt === CONTEXT_ENVS_MAX_ATTEMPTS) {
                        console.error('Failed to load context envs', err);
                        setLoading(false);
                        setFailed(true);
                        return;
                    }
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise(resolve => {
                        setTimeout(resolve, CONTEXT_ENVS_RETRY_DELAY_MS);
                    });
                    if (controller.signal.aborted) return;
                }
            }
        })();
        return () => controller.abort();
    }, []);

    return { envs, loading, failed };
};

export default useContextEnvs;
